import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils";

export type SaleStep = "department" | "reference" | "details" | "collect";

export interface SaleStepIndicatorProps {
  readonly step: SaleStep;
  readonly hasDepartment: boolean;
  /** When the reference came in pre-supplied (Inventory's Take Deposit
   *  etc.), the wizard skips the reference step entirely. Drop it
   *  from the indicator so the "Step N of M" copy stays honest. */
  readonly hasReference?: boolean;
}

export function SaleStepIndicator({
  step,
  hasDepartment,
  hasReference = true,
}: SaleStepIndicatorProps) {
  const { t } = useTranslation();
  const order: SaleStep[] = [
    ...(hasDepartment ? (["department"] as const) : ([] as const)),
    ...(hasReference ? (["reference"] as const) : ([] as const)),
    "details",
    "collect",
  ];
  // Short labels - used inline next to the numbered pill. Long-form
  // labels stay on aria-label so screen readers still announce the
  // full meaning (WCAG SC 2.4.6). The 4-step variant overflowed at
  // ~1280px because "Customer & amount" and "Take payment" wrapped to
  // two lines (2026-05-02 dogfood); shorter labels + ``whitespace-nowrap``
  // keep every step on one line down to the small-laptop range.
  const shortLabels: Record<SaleStep, string> = {
    department: t("sale.wizard.stepDepartment"),
    reference: t("sale.wizard.stepReferenceShort"),
    details: t("sale.wizard.stepDetailsShort"),
    collect: t("sale.wizard.stepCollectShort"),
  };
  const longLabels: Record<SaleStep, string> = {
    department: t("sale.wizard.stepDepartment"),
    reference: t("sale.wizard.stepReference"),
    details: t("sale.wizard.stepDetails"),
    collect: t("sale.wizard.stepCollect"),
  };
  const idx = order.indexOf(step);
  const totalSteps = order.length;
  const stepNumber = idx + 1;
  // Mobile progress: percentage of the bar that's filled. Active step
  // counts as half-done so the bar visibly advances when the cashier
  // *enters* a step, not only when they leave it.
  const progressPercent = Math.min(100, Math.round(((idx + 0.5) / totalSteps) * 100));
  return (
    <>
      {/* Mobile (< md) - circles-only read as bare/odd at 414px wide
          (2026-05-03 dogfood). Replace with a "Step N of M · Label"
          line + a thin progress bar - same info, more legible, takes
          less vertical room. The visually-hidden ol below stays in
          the DOM at all viewports so screen-reader users get the full
          ordered-list semantics. */}
      <div className="flex flex-col gap-1.5 md:hidden">
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {t("sale.wizard.stepProgress", { current: stepNumber, total: totalSteps })}
          </span>
          <span className="font-semibold text-strong">{shortLabels[step]}</span>
        </div>
        <div
          className="h-1 overflow-hidden rounded-full bg-surface-muted"
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${longLabels[step]} - ${t("sale.wizard.stepStateCurrent")}`}
        >
          <span
            className="block h-full rounded-full bg-primary transition-[width] duration-200"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* md+ - full circle + label list. ``overflow-x-auto`` clipped
          the active step's ring at the top (rings render OUTSIDE the
          element's bounding box). Add ``py-1`` so the 1px ring has
          vertical room to breathe, and pair with ``overflow-y-visible``
          to make sure the clip is strictly horizontal. */}
      <ol className="hidden items-center gap-1.5 overflow-x-auto overflow-y-visible py-1 text-sm md:flex">
        {order.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          const stateWord = done
            ? t("sale.wizard.stepStateCompleted")
            : active
              ? t("sale.wizard.stepStateCurrent")
              : t("sale.wizard.stepStateUpcoming");
          return (
            <li key={s} className="flex shrink-0 items-center gap-1.5">
              <span
                role="img"
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                  done && "bg-strong text-inverse",
                  active &&
                    "border-2 border-primary bg-primary/10 text-primary dark:bg-primary dark:text-primary-foreground",
                  !done && !active && "bg-surface-muted text-muted",
                )}
                aria-current={active ? "step" : undefined}
                aria-label={`${longLabels[s]} - ${stateWord}`}
              >
                {done ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : i + 1}
              </span>
              <span
                className={cn(
                  "whitespace-nowrap",
                  active ? "font-semibold text-strong" : "text-muted",
                )}
              >
                {shortLabels[s]}
              </span>
              {i < order.length - 1 && (
                <span className="mx-0.5 h-px w-4 shrink-0 bg-line sm:w-6" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
    </>
  );
}
